import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser } from '@/types/auth';
import type { AuthRole } from '@/types/models';

export interface AuthState {
	user: AuthUser | null;
	role: AuthRole | null;
	isAuthenticated: boolean;
	mustChangePassword: boolean;
}

const initialState: AuthState = {
	user: null,
	role: null,
	isAuthenticated: false,
	mustChangePassword: false,
};

// Hydrate on startup from localStorage
function hydrateFromStorage(): AuthState {
	try {
		const storedUser = localStorage.getItem('alforaa_user');
		const storedRole = localStorage.getItem('alforaa_role');
		const storedToken = localStorage.getItem('alforaa_access_token');
		const storedMustChange =
			localStorage.getItem('alforaa_must_change_password') === 'true';

		if (storedUser && storedRole && storedToken) {
			const user = JSON.parse(storedUser) as AuthUser;
			return {
				user,
				role: storedRole as AuthRole,
				isAuthenticated: true,
				mustChangePassword: storedMustChange,
			};
		}
	} catch {
		localStorage.clear();
	}
	return initialState;
}

const authSlice = createSlice({
	name: 'auth',
	initialState: hydrateFromStorage(),
	reducers: {
		setCredentials(
			state,
			action: PayloadAction<{
				user: AuthUser;
				role: AuthRole;
				accessToken: string;
				refreshToken: string;
				mustChangePassword?: boolean;
			}>,
		) {
			const {
				user,
				role,
				accessToken,
				refreshToken,
				mustChangePassword = false,
			} = action.payload;
			state.user = user;
			state.role = role;
			state.isAuthenticated = true;
			state.mustChangePassword = mustChangePassword;
			const loginRoutes: Record<string, string> = {
				student: '/student/login',
				parent: '/parent/login',
				trainer: '/trainer/login',
				school_admin: '/school/login',
				master_admin: '/admin/login',
				salesperson: '/sales/login',
				sales_manager: '/sales/login',
				appointment_coordinator: '/sales/login',
			};
			localStorage.setItem('alforaa_access_token', accessToken);
			localStorage.setItem('alforaa_refresh_token', refreshToken);
			localStorage.setItem('alforaa_user', JSON.stringify(user));
			localStorage.setItem('alforaa_role', role);
			localStorage.setItem(
				'alforaa_login_path',
				loginRoutes[role] ?? '/student/login',
			);
			localStorage.setItem(
				'alforaa_must_change_password',
				String(mustChangePassword),
			);
		},
		patchUser(state, action: PayloadAction<Partial<AuthUser>>) {
			if (!state.user) return;
			state.user = { ...state.user, ...action.payload };
			localStorage.setItem('alforaa_user', JSON.stringify(state.user));
		},
		completedSetup(state) {
			state.mustChangePassword = false;
			localStorage.setItem('alforaa_must_change_password', 'false');
		},
		clearCredentials(state) {
			state.user = null;
			state.role = null;
			state.isAuthenticated = false;
			state.mustChangePassword = false;
			// Preserve keys that should survive logout
			const preserve: Record<string, string | null> = {
				studentIdPrefix: localStorage.getItem('studentIdPrefix'),
				alforaa_login_path: localStorage.getItem('alforaa_login_path'),
				faceLoginSchoolId: localStorage.getItem('faceLoginSchoolId'),
				faceLoginSchoolName: localStorage.getItem('faceLoginSchoolName'),
			};
			localStorage.clear();
			for (const [key, value] of Object.entries(preserve)) {
				if (value !== null) localStorage.setItem(key, value);
			}
		},
	},
});

export const { setCredentials, patchUser, completedSetup, clearCredentials } =
	authSlice.actions;
export default authSlice.reducer;
